// The "What we love" homepage section (sections/what-we-love.liquid). The section renders every tab.
// This element switches tabs, expands a video and plays it, and gives one pause button for all videos
// (WCAG 2.2.2). Rules:
// - Only the expanded video in the open tab plays, and only while "playing" is on.
// - With reduced motion, "playing" starts off. The cover images show until the shopper presses Play.
// - Pause holds everywhere: in other tabs, and on a side video the shopper expands (it shows its cover
//   image). Only the pause/play button starts the videos again. A tab opens on its center video.
// - Videos play only while the section is within one screen height of the window. A video loads only
//   when it first plays.
'use strict';

(() => {
  if (customElements.get('what-we-love')) return;

  const EXPAND_CLASSES = { 1: 'expand-left', 2: 'expand-center', 3: 'expand-right' };

  class WhatWeLove extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.playing = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.inView = false;

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      // In the theme editor, selecting a block opens its tab.
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.panels.indexOf(event.target);
          if (index >= 0) this.showTab(index);
        },
        { signal },
      );

      // Nothing plays or loads until the section is one screen height away. On a slow connection a video
      // needs time to load, so it loads while the shopper scrolls toward it. Shoppers who stop higher up
      // load nothing.
      // Scrolled further away than that, the video pauses, so it stops using data and battery.
      this.observer = new IntersectionObserver(
        (entries) => {
          this.inView = entries[entries.length - 1].isIntersecting;
          this.update();
        },
        { rootMargin: '100% 0px' },
      );
      this.observer.observe(this);

      this.showPlayingState();
    }

    disconnectedCallback() {
      this.abortController.abort();
      this.observer.disconnect();
      this.querySelectorAll('.video-container').forEach((card) => this.pause(card, true));
    }

    get chips() {
      return Array.from(this.querySelectorAll('[data-tab]'));
    }

    get panels() {
      return Array.from(this.querySelectorAll('[data-tab-panel]'));
    }

    get activePanel() {
      return this.panels.find((panel) => !panel.hidden);
    }

    onClick(event) {
      const chip = event.target.closest('[data-tab]');
      if (chip) {
        this.showTab(this.chips.indexOf(chip));
        return;
      }

      const showButton = event.target.closest('[data-show-video]');
      if (showButton && !showButton.disabled) {
        const card = showButton.closest('.video-container');
        const fromKeyboard = showButton.matches(':focus-visible');
        this.expand(card);
        this.update();
        // The button just pressed is now disabled, so keyboard focus would drop to the page. Move it to the
        // pause/play button of the expanded video. Only for the keyboard: focus() scrolls the page to the
        // button, and after a tap that made the page jump down.
        if (fromKeyboard) {
          (card.querySelector('[data-playing-toggle]') || card.querySelector('.shop-collection-btn'))?.focus();
        }
        return;
      }

      if (event.target.closest('[data-playing-toggle]')) {
        this.playing = !this.playing;
        this.showPlayingState();
        this.update();
      }
    }

    showTab(index) {
      this.chips.forEach((chip, i) => chip.setAttribute('aria-pressed', i === index ? 'true' : 'false'));
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
      const center = this.panels[index]?.querySelector('[data-video="2"]');
      if (center) this.expand(center);
      this.update();
    }

    expand(card) {
      const panel = card.closest('[data-tab-panel]');
      panel.querySelectorAll('.video-container').forEach((other) => {
        const expanded = other === card;
        other.toggleAttribute('data-expanded', expanded);
        const showButton = other.querySelector('[data-show-video]');
        if (showButton) {
          showButton.disabled = expanded;
          if (expanded) {
            showButton.setAttribute('aria-hidden', 'true');
          } else {
            showButton.removeAttribute('aria-hidden');
          }
        }
      });
      panel.classList.remove(...Object.values(EXPAND_CLASSES));
      panel.classList.add(EXPAND_CLASSES[card.dataset.video]);
    }

    // The expanded video of the open tab: the only one that may play.
    isOpen(card, activePanel = this.activePanel) {
      return card.hasAttribute('data-expanded') && card.closest('[data-tab-panel]') === activePanel;
    }

    shouldPlay(card, activePanel = this.activePanel) {
      return this.inView && this.playing && this.isOpen(card, activePanel);
    }

    update() {
      const activePanel = this.activePanel;
      this.querySelectorAll('.video-container').forEach((card) => {
        // An uploaded video with no cover image would show an empty card. Near the screen, in the open tab,
        // let it load its first frame.
        const video = card.querySelector('video');
        if (video && this.inView && video.preload === 'none' && !card.querySelector('.video-cover')) {
          if (card.closest('[data-tab-panel]') === activePanel) video.preload = 'metadata';
        }

        if (this.shouldPlay(card, activePanel)) {
          this.play(card);
        } else {
          // Pause keeps the current frame on the expanded video. A video that is out of view gets its
          // cover image back.
          this.pause(card, !this.isOpen(card, activePanel));
        }
      });
    }

    // The cover hides when the video reports "playing", when it has a frame to show.
    play(card) {
      const video = card.querySelector('video');
      if (!video) return;
      if (!video.svahaWatched) {
        video.addEventListener('playing', () => this.onPlaying(card));
        video.svahaWatched = true;
      }
      video.play().catch(() => this.onRefused(card));
    }

    onPlaying(card) {
      // The shopper can pause or switch tabs while the video starts.
      if (!this.shouldPlay(card)) {
        this.update();
        return;
      }
      const cover = card.querySelector('.video-cover');
      if (cover) cover.hidden = true;
    }

    // The browser can refuse to play, for example in low power mode. Show the play state, so the button
    // tells the truth and the shopper can try again. A pause during the start also rejects play(); then
    // the video should not play anyway, so nothing changes.
    onRefused(card) {
      if (!this.shouldPlay(card)) return;
      this.playing = false;
      this.showPlayingState();
    }

    pause(card, showCover) {
      card.querySelector('video')?.pause();
      const cover = card.querySelector('.video-cover');
      if (cover && showCover) cover.hidden = false;
    }

    showPlayingState() {
      const label = this.playing ? this.dataset.labelPause : this.dataset.labelPlay;
      this.querySelectorAll('[data-playing-toggle]').forEach((toggle) => {
        toggle.dataset.playing = this.playing ? 'true' : 'false';
        toggle.querySelector('[data-playing-label]').textContent = label;
      });
    }
  }

  customElements.define('what-we-love', WhatWeLove);
})();
